// statistics_clinic_04_practice_normal_distribution_zscores.md — 3 DIY cases,
// each comparing two values from different scales/groups where the raw
// numbers mislead and only a z-score comparison reveals who is actually
// relatively higher.

export const STATS_CLINIC_04_TRACKER_ID = 'statistics-clinic-04-practice-bank';

export const STATS_CLINIC_04_LENSES = [
  ['business_question', 'Business question'],
  ['statistic_family', 'Statistic family'],
  ['statistic_chosen', 'Statistic chosen'],
  ['why_statistic', 'Why this statistic'],
  ['calculation', 'Calculation'],
  ['result', 'Result'],
  ['meaning', 'Meaning'],
  ['decision', 'Decision'],
  ['caution', 'Caution'],
];

export const STATS_CLINIC_04_ITEMS = [
  {
    id: 'DIY01',
    title: 'DIY Practice 01: Candidate screening across two test platforms',
    scenario: "A recruiter is comparing two final-round candidates who happened to take different aptitude tests, provided by different vendors with very different scoring scales. The recruiter wants to know who scored better relative to their own test's typical taker, not just who has the bigger number.",
    tables: [{
      columns: ['candidate', 'test', 'score', 'test_mean', 'test_sd'],
      rows: [['Candidate X', 'Test A', '720', '650', '50'], ['Candidate Y', 'Test B', '88', '75', '8']],
    }],
    answerGuide: {
      business_question: 'Which candidate scored better relative to their own test?',
      statistic_family: 'Standardised comparison.',
      statistic_chosen: 'Z-score.',
      why_statistic: 'The two tests have very different scales, means, and spreads, so the raw scores (720 vs 88) cannot be compared directly.',
      calculation: 'z(X) = (720-650)/50 = 70/50 = 1.4. z(Y) = (88-75)/8 = 13/8 = 1.625.',
      result: "Candidate Y's z-score (1.625) is higher than Candidate X's (1.4).",
      meaning: "Candidate Y performed better relative to their own test's typical taker, despite a much lower raw score (88 vs 720).",
      decision: 'Advance Candidate Y on relative performance grounds, not Candidate X on the larger raw number.',
      caution: 'Never compare raw scores from two different tests directly — always standardise against each test\'s own mean and SD first.',
    },
    ruleTerms: {
      business_question: ['scored better', 'relative'],
      statistic_family: ['standardised', 'standardized', 'comparison'],
      statistic_chosen: ['z-score', 'z score'],
      why_statistic: ['different scale', 'cannot', 'directly'],
      calculation: ['1.4', '1.625'],
      result: ['1.625', 'higher'],
      meaning: ['candidate y', 'despite', 'lower'],
      decision: ['advance', 'candidate y'],
      caution: ['never compare raw', 'standardise'],
    },
    criticalTerms: { calculation: ['1.4', '1.625'], result: ['1.625'] },
  },
  {
    id: 'DIY02',
    title: 'DIY Practice 02: Top student across two cohort years',
    scenario: "A school wants to award a \"most improved relative to peers\" certificate and is comparing the top exam score from this year's cohort against the top exam score from last year's cohort. Each cohort's exam had a different average and spread.",
    tables: [{
      columns: ['student', 'cohort_year', 'score', 'cohort_mean', 'cohort_sd'],
      rows: [['Student P', '2025', '92', '78', '7'], ['Student Q', '2026', '95', '85', '6']],
    }],
    answerGuide: {
      business_question: 'Which student performed better relative to their own cohort?',
      statistic_family: 'Standardised comparison.',
      statistic_chosen: 'Z-score.',
      why_statistic: 'The two cohorts had different average scores and spread, so comparing raw exam scores directly is unfair.',
      calculation: 'z(P) = (92-78)/7 = 14/7 = 2.0. z(Q) = (95-85)/6 = 10/6 = 1.67.',
      result: "Student P's z-score (2.0) is higher than Student Q's (1.67).",
      meaning: 'Student P performed better relative to their own cohort, despite a lower raw score (92 vs 95).',
      decision: 'Award the certificate based on relative performance (Student P), not the higher raw score (Student Q).',
      caution: "Do not assume a higher raw score means better performance when the two students sat different cohorts' exams.",
    },
    ruleTerms: {
      business_question: ['performed better', 'relative', 'cohort'],
      statistic_family: ['standardised', 'standardized', 'comparison'],
      statistic_chosen: ['z-score', 'z score'],
      why_statistic: ['different average', 'unfair'],
      calculation: ['2.0', '1.67'],
      result: ['2.0', 'higher'],
      meaning: ['student p', 'despite', 'lower'],
      decision: ['student p', 'certificate'],
      caution: ['do not assume', 'different cohort'],
    },
    criticalTerms: { calculation: ['2.0', '1.67'], result: ['2.0'] },
  },
  {
    id: 'DIY03',
    title: 'DIY Practice 03: Performance review across two managers',
    scenario: "An HR partner is deciding which of two employees to put forward for a cross-team promotion panel. The employees are rated by different managers, and those managers' teams have noticeably different average ratings and spread — one manager rates more generously than the other.",
    tables: [{
      columns: ['employee', 'manager', 'rating', 'manager_team_mean', 'manager_team_sd'],
      rows: [['Employee 1', 'Manager Green', '4.1', '3.2', '0.5'], ['Employee 2', 'Manager Patel', '4.3', '3.8', '0.3']],
    }],
    answerGuide: {
      business_question: 'Which employee performed better relative to their own manager\'s team?',
      statistic_family: 'Standardised comparison.',
      statistic_chosen: 'Z-score.',
      why_statistic: "The two managers' teams have different average ratings and spread, so comparing raw ratings directly rewards the more generous manager's team unfairly.",
      calculation: 'z(Employee 1) = (4.1-3.2)/0.5 = 0.9/0.5 = 1.8. z(Employee 2) = (4.3-3.8)/0.3 = 0.5/0.3 = 1.67.',
      result: "Employee 1's z-score (1.8) is higher than Employee 2's (1.67).",
      meaning: "Employee 1 stands out more relative to their own manager's team, despite a lower raw rating (4.1 vs 4.3).",
      decision: 'Put Employee 1 forward on relative-standout grounds, not Employee 2 on the higher raw rating.',
      caution: "A more generous manager's raw ratings can make an average employee look better than a genuinely standout employee under a stricter manager — always standardise first.",
    },
    ruleTerms: {
      business_question: ['performed better', 'relative', 'manager'],
      statistic_family: ['standardised', 'standardized', 'comparison'],
      statistic_chosen: ['z-score', 'z score'],
      why_statistic: ['different average', 'generous', 'unfair'],
      calculation: ['1.8', '1.67'],
      result: ['1.8', 'higher'],
      meaning: ['employee 1', 'despite', 'lower'],
      decision: ['employee 1', 'forward'],
      caution: ['generous manager', 'standardise'],
    },
    criticalTerms: { calculation: ['1.8', '1.67'], result: ['1.8'] },
  },
  {
    id: 'DIY04',
    title: 'DIY Practice 04: Support-team threshold setting',
    scenario: 'A support lead wants to set a "top 10%" weekly recognition threshold for tickets resolved. The distribution of weekly ticket counts is roughly normal.',
    tables: [{
      columns: ['metric', 'value'],
      rows: [['Mean tickets resolved', '46'], ['Standard deviation', '8'], ['Top 10% z benchmark', '1.28']],
    }],
    answerGuide: {
      business_question: 'What ticket count should mark roughly the top 10% of weekly performance?',
      statistic_family: 'Normal threshold setting / standardised cutoff.',
      statistic_chosen: 'Reverse z-score formula.',
      why_statistic: 'The question starts with a percentile target and asks for the raw value that corresponds to that z benchmark.',
      calculation: 'value = mean + (z x SD) = 46 + (1.28 x 8) = 46 + 10.24 = 56.24.',
      result: 'The top-10% threshold is about 56.24 tickets, so a practical whole-ticket cutoff is 57.',
      meaning: 'A learner resolving 57 or more tickets is around the top 10% if the normal assumption is reasonable.',
      decision: 'Set the recognition threshold at 57 tickets, or use the observed 90th percentile if an exact top 10% headcount is required.',
      caution: 'This assumes weekly ticket counts are roughly normal; if the distribution is skewed or capped by assignment rules, use percentiles instead.',
    },
    ruleTerms: {
      business_question: ['top 10%', 'threshold'],
      statistic_family: ['threshold', 'cutoff'],
      statistic_chosen: ['reverse', 'z'],
      why_statistic: ['percentile', 'raw value'],
      calculation: ['56.24', '46', '1.28'],
      result: ['57'],
      meaning: ['top 10%'],
      decision: ['57', 'threshold'],
      caution: ['normal', 'percentile'],
    },
    criticalTerms: { calculation: ['56.24'], result: ['57'] },
  },
  {
    id: 'DIY05',
    title: 'DIY Practice 05: Delivery depot comparison',
    scenario: 'Two depots report late-delivery rates this week. Depot East has the higher raw percentage, but the operations director wants to know which depot is more unusual relative to its own history.',
    tables: [{
      columns: ['depot', 'current_late_rate', 'usual_mean', 'usual_sd'],
      rows: [['Depot East', '9.0%', '7.5%', '1.2%'], ['Depot West', '8.0%', '5.0%', '1.5%']],
    }],
    answerGuide: {
      business_question: 'Which depot is more unusual relative to its own usual late-delivery pattern?',
      statistic_family: 'Standardised comparison.',
      statistic_chosen: 'Z-score.',
      why_statistic: 'The depots have different usual late rates and volatility, so the higher raw percentage is not automatically the bigger signal.',
      calculation: 'z(East) = (9.0-7.5)/1.2 = 1.25. z(West) = (8.0-5.0)/1.5 = 2.0.',
      result: "Depot West's z-score (2.0) is higher than Depot East's (1.25).",
      meaning: 'Depot West is more unusual relative to its own history even though Depot East has the higher raw late rate.',
      decision: 'Investigate Depot West first for a possible process issue, while still monitoring East because its raw rate remains high.',
      caution: 'Check shipment volume and any known disruption before treating the z-score as a root-cause finding.',
    },
    ruleTerms: {
      business_question: ['depot', 'unusual', 'history'],
      statistic_family: ['standardised', 'standardized', 'comparison'],
      statistic_chosen: ['z-score', 'z score'],
      why_statistic: ['different usual', 'volatility'],
      calculation: ['1.25', '2.0'],
      result: ['west', '2.0'],
      meaning: ['west', 'more unusual'],
      decision: ['investigate', 'west'],
      caution: ['volume', 'disruption'],
    },
    criticalTerms: { calculation: ['1.25', '2.0'], result: ['2.0'] },
  },
];

export const STATS_CLINIC_04_CONFIG = {
  trackerId: STATS_CLINIC_04_TRACKER_ID,
  resourceKey: 'statistics_clinic_04_practice_normal_distribution_zscores',
  rubricKey: 'statistics_normal_distribution_zscores',
  activityScope: 'daily_concept_clinic_take_home',
  heading: 'Learner Practice: Savable Normal Distribution & Z-Score Cases',
  description: 'Each case asks you to use z-scores for a real analyst decision: fair comparison across groups, threshold setting from a percentile target, or escalation based on what is unusual for that baseline. Your work autosaves to your own practice record.',
  introAlert: 'A z-score is a universal ruler. Use it to compare values against their own baselines, then translate the result into a decision.',
  decisionFramework: {
    title: 'Flagged decision framework: z-score findings',
    description: 'A z-score is not the final answer. Learners must translate the distance from the baseline into a review, escalation, or fair-comparison decision.',
    rules: [
      '|z| below 2: usually ordinary variation; explain the value in context rather than escalating.',
      '|z| between 2 and 3: unusual enough to mention; check volume, context, and business impact.',
      '|z| above 3: flag for review; the value is statistically unusual but may still be valid.',
      '|z| above 4: escalate to the data owner or manager; likely data-quality issue or exceptional event.',
      '|z| above 5: assume data-entry or system error until confirmed by the source owner, especially in clinical or regulated data.',
    ],
    responseFrame: 'Frame the response as: "The z-score is [value], which falls into the [ordinary / unusual / review / escalation] band. That means the value is [business meaning]. I would [compare fairly / flag / escalate / confirm with source owner] before [using / excluding] the record."',
  },
  lenses: STATS_CLINIC_04_LENSES,
  workingField: null,
  items: STATS_CLINIC_04_ITEMS,
};
